/** One calling convention for every user-facing Replace entrance. */

import { store } from '../core/state.js';
import { eventBus } from '../core/events.js';
import { isSectionLockedByPeer, lockKeyForInstance } from './collab-guard.js';
import { resolveSectionByInstance } from './section-utils.js';
import { escapeForAttrSelector } from './editable-utils.js';
import { extractSectionCssBackgroundImages } from '../shared/css-background-images.js';
import { isDomTarget, isSectionCssTarget, TARGET_DOMAIN } from '../shared/target-ref.js';
import { replaceKindFor } from './replace-target.js';
import { classifyElementKind } from '../shared/element-kind.js';

// A repair retains the first occurrence's HTML. Neither its fingerprint nor
// restored provenance after Undo can make an already-open callback current again.
const retiredInstances = new Map();
let retirementProject = null;
function projectGeneration() {
  return `${store.getState()?.projectId ?? ''}:${store.getEpoch() - store.getRevision()}`;
}
function identityStamp(instanceId, section) {
  const project = projectGeneration();
  if (retirementProject !== project) {
    retiredInstances.clear();
    retirementProject = project;
  }
  return JSON.stringify([
    project,
    retiredInstances.get(instanceId) || 0,
    Object.hasOwn(section, 'editableIdRepairs'),
    section.editableIdRepairs
  ]);
}
eventBus.on('editable-ids:repaired', ({ instanceIds = [] } = {}) => {
  // Refresh the project boundary before recording this retirement.
  identityStamp('', {});
  for (const instanceId of instanceIds) {
    retiredInstances.set(instanceId, (retiredInstances.get(instanceId) || 0) + 1);
  }
});

function sameKindSlots(target, slots) {
  return slots.every((slot) => (slot?.kind || target?.kind) === target?.kind);
}

function domFingerprint(element) {
  const source = String(element?.outerHTML || '');
  let hash = 2166136261;
  for (let index = 0; index < source.length; index++) {
    hash ^= source.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return (hash >>> 0).toString(16).padStart(8, '0');
}

export function validateReplacementTarget(target, { mimeType = '' } = {}) {
  if (!target?.instanceId || !target?.targetRef) return { ok: false, reason: 'invalid-target' };
  if (![TARGET_DOMAIN.DOM, TARGET_DOMAIN.SECTION_CSS].includes(target.targetRef.domain)) {
    return { ok: false, reason: 'invalid-target' };
  }
  if (typeof store.isWritable === 'function' && !store.isWritable()) {
    return { ok: false, reason: 'read-only' };
  }
  if (isSectionLockedByPeer(lockKeyForInstance(target.instanceId))) {
    return { ok: false, reason: 'peer-locked' };
  }
  if (mimeType && !/^image\//i.test(mimeType)) {
    return { ok: false, reason: 'unsupported-mime' };
  }
  const resolved = resolveSectionByInstance(target?.instanceId);
  if (!resolved?.section) return { ok: false, reason: 'section-missing' };
  const currentIdentity = identityStamp(target.instanceId, resolved.section);
  if (
    target.expectedReplacementIdentity &&
    target.expectedReplacementIdentity !== currentIdentity
  ) {
    return { ok: false, reason: 'target-stale' };
  }
  if (isDomTarget(target)) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(`<div>${resolved.section.html || ''}</div>`, 'text/html');
    const root = doc.body.firstElementChild;
    const eid = target.targetRef?.eid || target.eid;
    const currentElement = eid
      ? root?.querySelector(`[data-pb-eid="${escapeForAttrSelector(eid)}"]`)
      : null;
    if (!currentElement) {
      return { ok: false, reason: 'target-stale' };
    }
    const currentFingerprint = domFingerprint(currentElement);
    if (
      target.expectedTargetFingerprint &&
      target.expectedTargetFingerprint !== currentFingerprint
    ) {
      return { ok: false, reason: 'target-stale' };
    }
    const currentKind = classifyElementKind(currentElement, { editableType: target.editableType });
    if (target.kind && target.kind !== 'unknown' && currentKind !== target.kind) {
      return { ok: false, reason: 'target-kind-changed' };
    }
    return { ok: true, resolved, currentFingerprint, currentIdentity };
  }
  if (isSectionCssTarget(target)) {
    const fact = extractSectionCssBackgroundImages(
      resolved.section.css || '',
      resolved.section.sectionId || ''
    ).find((entry) => entry.key === target.targetRef.key);
    if (!fact || fact.expectedFingerprint !== target.targetRef.expectedFingerprint) {
      return { ok: false, reason: 'target-stale' };
    }
    if (!fact.writable) return { ok: false, reason: fact.reason || 'target-read-only' };
  }
  return { ok: true, resolved, currentIdentity };
}

export function snapshotReplacementTarget(target) {
  const current = validateReplacementTarget(target);
  if (!current.ok) return current;
  return {
    ok: true,
    target: {
      ...target,
      expectedReplacementIdentity: current.currentIdentity,
      ...(current.currentFingerprint
        ? { expectedTargetFingerprint: current.currentFingerprint }
        : {})
    }
  };
}

async function defaultAdapter(target) {
  const replaceKind = replaceKindFor(target);
  if (replaceKind === 'logo') {
    const logo = await import('./logo-editor.js');
    if (!validateReplacementTarget(target).ok) return { status: 'cancelled' };
    logo.setActiveLogoEdit(target);
    if (target.context?.catalog === 'svg') {
      await logo.replaceLogoFromSvgLibrary(target.context);
      const images = await import('./image-editor.js');
      return images.waitForSvgModalOutcome();
    }
    return logo.replaceLogo(target.context || {});
  }
  if (replaceKind === 'decoration' || replaceKind === 'icon') {
    const images = await import('./image-editor.js');
    if (!validateReplacementTarget(target).ok) return { status: 'cancelled' };
    const triggerEl = target.context?.triggerEl || null;
    const context = typeof target.context === 'string' ? target.context : '';
    if (target.editableType === 'image') {
      await images.openDecorativeSvgLibraryForTarget(target, { triggerEl });
      return images.waitForSvgModalOutcome();
    }
    images.setActiveSvgEdit(target);
    if (target.kind === 'decoration') {
      await images.openDecorativeSvgLibraryForActiveSvg({
        slots: target.slots?.length > 1 ? target.slots : null,
        context,
        triggerEl
      });
    } else {
      await images.openSvgLibrary({ triggerEl });
    }
    return images.waitForSvgModalOutcome();
  }
  if (!replaceKind) return { status: 'cancelled' };
  const images = await import('./image-editor.js');
  return images.replaceImage(target);
}

export async function openReplaceDialog({ target, slots = [target], context, onPick } = {}) {
  if (!target?.instanceId || !target?.targetRef) {
    return { status: 'skipped', reason: 'invalid-target' };
  }
  const queue = Array.isArray(slots) && slots.length ? slots : [target];
  if (!sameKindSlots(target, queue)) {
    return { status: 'skipped', reason: 'mixed-kind-slots' };
  }
  const guardedTargetResult = snapshotReplacementTarget(target);
  if (!guardedTargetResult.ok) {
    return { status: 'skipped', reason: guardedTargetResult.reason };
  }
  const guardedTarget = guardedTargetResult.target;
  const guardedQueue = [];
  for (const slot of queue) {
    const guardedSlotResult = snapshotReplacementTarget({ ...guardedTarget, ...slot });
    if (!guardedSlotResult.ok) {
      return { status: 'skipped', reason: guardedSlotResult.reason };
    }
    const guardedSlot = guardedSlotResult.target;
    guardedQueue.push({
      ...guardedSlot,
      ...(slot.data
        ? {
            data: {
              ...slot.data,
              ...(slot.data.target ? { target: guardedSlot } : {})
            }
          }
        : {})
    });
  }

  try {
    const result = await defaultAdapter({ ...guardedTarget, slots: guardedQueue, context });
    const status = result?.status === 'picked' ? 'picked' : 'cancelled';
    const outcome = { status, target: guardedTarget };
    if (status === 'picked' && typeof onPick === 'function') await onPick(outcome);
    return outcome;
  } catch (error) {
    console.error('[replace-dispatcher] dialog failed', error);
    return { status: 'cancelled', reason: 'dialog-error', target: guardedTarget };
  }
}
